'use client';

import { useState, useEffect } from 'react';

// ============================================================================
// Types
// ============================================================================

export type SubscriptionEstimate = '1-3' | '4-6' | '7-10' | '10+' | 'unknown';

interface DiagnosticQuestionProps {
  onAnswer: (estimate: SubscriptionEstimate) => void;
  pageSlug: string;
}

// ============================================================================
// Response copy based on selection
// ============================================================================

const responseCopy: Record<SubscriptionEstimate, string> = {
  '1-3': "Most people who think they have 1–3 actually have more.",
  '4-6': "That's already more than most people remember.",
  '7-10': "At this point, renewals usually slip through.",
  '10+': "Almost nobody tracks all of these reliably.",
  'unknown': "That's exactly why subscriptions get expensive quietly.",
};

// ============================================================================
// localStorage helpers
// ============================================================================

const STORAGE_KEY = 'repetix_diagnostic';

export function saveDiagnosticAnswer(estimate: SubscriptionEstimate, pageSlug: string) {
  try {
    const data = {
      estimate,
      pageSlug,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage not available
  }
}

export function getDiagnosticAnswer(): { estimate: SubscriptionEstimate; pageSlug: string } | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

export function clearDiagnosticAnswer() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage not available
  }
}

// ============================================================================
// Component
// ============================================================================

export function DiagnosticQuestion({ onAnswer, pageSlug }: DiagnosticQuestionProps) {
  const [selected, setSelected] = useState<SubscriptionEstimate | null>(null);
  const [showResponse, setShowResponse] = useState(false);

  const options: { value: SubscriptionEstimate; label: string }[] = [
    { value: '1-3', label: '1–3' },
    { value: '4-6', label: '4–6' },
    { value: '7-10', label: '7–10' },
    { value: '10+', label: '10+' },
    { value: 'unknown', label: "I don't know" },
  ];

  const handleSelect = (value: SubscriptionEstimate) => {
    setSelected(value);
  };

  const handleSubmit = () => {
    if (!selected) return;

    // Save to localStorage
    saveDiagnosticAnswer(selected, pageSlug);

    // Track analytics event
    if (typeof window !== 'undefined' && (window as any).posthog) {
      (window as any).posthog.capture('diagnostic_answered', {
        estimate: selected,
        page_slug: pageSlug,
      });
    }

    // Show response briefly
    setShowResponse(true);

    // After delay, proceed
    setTimeout(() => {
      onAnswer(selected);
    }, 800);
  };

  if (showResponse && selected) {
    return (
      <div className="text-center py-8 animate-in fade-in duration-300">
        <p className="text-lg text-white/80">
          {responseCopy[selected]}
        </p>
      </div>
    );
  }

  return (
    <div className="text-center py-8">
      <h2 className="text-2xl font-bold text-white mb-8">
        How many subscriptions do you think you're paying for?
      </h2>

      <div className="flex flex-wrap justify-center gap-3 mb-8">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option.value)}
            className={`
              px-6 py-3 rounded-lg font-medium transition-all
              ${selected === option.value
                ? 'bg-white text-black'
                : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
              }
            `}
          >
            {option.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!selected}
        className={`
          px-8 py-4 rounded-lg font-semibold text-lg transition-all
          ${selected
            ? 'bg-white text-black hover:bg-white/90'
            : 'bg-white/20 text-white/40 cursor-not-allowed'
          }
        `}
      >
        Show me
      </button>
    </div>
  );
}

// ============================================================================
// Dashboard callback component
// ============================================================================

interface DiagnosticCallbackProps {
  actualCount: number;
}

/**
 * Sync diagnostic answer from localStorage to database.
 * This triggers the personalized welcome email for new users.
 */
async function syncDiagnosticToServer(estimate: SubscriptionEstimate, pageSlug: string) {
  try {
    await fetch('/api/diagnostic', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ estimate, pageSlug }),
    });
  } catch {
    // Silently fail - not critical
  }
}

export function DiagnosticCallback({ actualCount }: DiagnosticCallbackProps) {
  const [diagnostic, setDiagnostic] = useState<{ estimate: SubscriptionEstimate; pageSlug: string } | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [hasSynced, setHasSynced] = useState(false);

  useEffect(() => {
    const stored = getDiagnosticAnswer();
    if (stored) {
      setDiagnostic(stored);

      // Sync to database once (triggers welcome email for new users)
      if (!hasSynced) {
        setHasSynced(true);
        syncDiagnosticToServer(stored.estimate, stored.pageSlug);
      }
    }
  }, [hasSynced]);

  if (!diagnostic || dismissed) return null;

  const estimateLabels: Record<SubscriptionEstimate, string> = {
    '1-3': '1–3',
    '4-6': '4–6',
    '7-10': '7–10',
    '10+': '10+',
    'unknown': 'unknown',
  };

  // Calculate if they're tracking more or fewer than expected
  const estimateRanges: Record<SubscriptionEstimate, { min: number; max: number }> = {
    '1-3': { min: 1, max: 3 },
    '4-6': { min: 4, max: 6 },
    '7-10': { min: 7, max: 10 },
    '10+': { min: 10, max: Infinity },
    'unknown': { min: 0, max: Infinity },
  };

  const range = estimateRanges[diagnostic.estimate];
  const isUnderTracking = actualCount < range.min;
  const isOverTracking = actualCount > range.max;

  // Subtle insight message
  let insightMessage = '';
  if (diagnostic.estimate === 'unknown') {
    insightMessage = actualCount === 0
      ? "Start adding them—that's the first step."
      : `Now you know: ${actualCount}.`;
  } else if (isUnderTracking && actualCount === 0) {
    insightMessage = 'Add your subscriptions to start tracking.';
  } else if (isUnderTracking) {
    insightMessage = 'There might be more hiding in your bank statement.';
  } else if (isOverTracking) {
    insightMessage = 'Already more than you expected.';
  }

  return (
    <div className="bg-[var(--accent)]/5 border border-[var(--accent)]/20 rounded-lg p-4 relative">
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          clearDiagnosticAnswer();
        }}
        className="absolute top-2 right-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        aria-label="Dismiss"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <p className="text-[13px] text-[var(--text-secondary)]">
        You thought you had around <span className="font-medium text-[var(--text-primary)]">{estimateLabels[diagnostic.estimate]}</span> subscriptions.
      </p>
      <p className="text-[15px] font-semibold text-[var(--text-primary)] mt-1">
        You're tracking <span className="text-[var(--accent)]">{actualCount}</span>.
      </p>
      {insightMessage && (
        <p className="text-[12px] text-[var(--text-muted)] mt-2">
          {insightMessage}
        </p>
      )}
    </div>
  );
}
